import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { HTTP_BASE_URL, StatusCode } from '@qtek/shared/models';

export type AvatarShape = 'default' | 'circle';
export type ThumbnailSize = 'md' | 'sm';

@Component({
  selector: 'qt-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlexLayoutModule, MatTooltipModule],
})
export class AvatarComponent {
  private readonly baseUrl = inject(HTTP_BASE_URL);

  readonly name = input<string>('');
  readonly isSmall = input<boolean>(false);
  readonly bigImage = input<boolean>(false);
  readonly thumbnailSize = input<ThumbnailSize | undefined>(undefined);
  readonly statusCode = input<StatusCode | undefined>(undefined);
  readonly backgroundColor = input<string>('');
  readonly shape = input<AvatarShape>('default');

  readonly imageId = input<string | null>(null);
  readonly imageLoadError = signal(false);
  readonly imageUrl = computed(() => {
    const id = this.imageId();
    if (!id) return '';

    const thumbSize = this.thumbnailSize();
    const thumbQuery = thumbSize ? `?thb=${thumbSize}` : '';
    return `${this.baseUrl}/api/v1/service/doc/${id}${thumbQuery}`;
  });

  constructor() {
    effect(() => {
      this.imageId();
      this.imageLoadError.set(false);
    });
  }

  onImageError(): void {
    this.imageLoadError.set(true);
  }

  readonly avatarLetters = computed(() => {
    return this.generateLetters(this.name());
  });

  readonly isStatusActive = computed(() => {
    const status = this.statusCode();
    return status !== StatusCode.PENDING;
  });

  private generateLetters(name: string): string {
    if (!name) return '';

    const normalizedName = name
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/[^\p{L}\p{M}\p{N}\s]/gu, '') // Keep only letters, marks, numbers, and spaces (Unicode-aware)
      .trim();

    if (!normalizedName) return '';

    const words = normalizedName.split(' ').filter(word => word.length > 0);

    if (words.length === 0) return '';

    if (words.length === 1) {
      const word = words[0];
      return word[0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  }
}
